import React from "react";
import { Button } from "@semo/shared-ui";
import { isEmbeddedInIframe, requestParentNavigate } from "@semo/shared-utils";
import { CreditCard } from "@semo/icons";

/**
 * 카드결제 가맹 가입 안내 배너 — 채팅방 위쪽에 붙는다.
 *
 * 발주기관이 카드로 계약했는데 공급사가 씨마켓 카드결제(이노페이) 가맹이 아니면 씨마켓
 * 결제창이 열리지 않는다. 종전에는 이 사실을 발주기관만 봤고(계약 체결 모달), 정작 가맹
 * 가입으로 상태를 풀 수 있는 공급사에게는 아무것도 가지 않았다.
 *
 * **메시지가 아니라 배너다.** 이건 "이런 일이 있었다"는 기록이 아니라 "지금 이 상태다"라는
 * 표시라서, 가입을 마치면 사실이 아니게 된다. 메시지로 남기면 그 문장이 대화에 영영 남아
 * 이미 풀린 문제를 아직 막혀 있는 것처럼 보이게 한다. 방을 열 때 상태를 다시 물어
 * 조건이 사라지면 배너도 같이 사라진다.
 *
 * 대화가 길어져도 위로 밀려 올라가지 않는 자리에 둔다 — 메시지였다면 새 메시지에 밀렸다.
 *
 * **문구는 「신청」과 「승인」을 구분한다.** 가입 신청과 결제 가능 사이에 이노페이 전자계약·
 * 심사가 있어 며칠 걸린다(씨마켓 확인, 2026-08-24). 「가입하면 바로 결제된다」로 읽히면
 * 공급사는 신청 직후 결제를 기다리고 발주기관은 이유 없이 막힌 것으로 본다. 이 배너가
 * 사라지는 시점(가맹 판정이 뒤집히는 때)도 신청이 아니라 승인완료이며, 씨마켓이 발주기관에게
 * 보내는 알림도 같은 시점이라 둘이 어긋나지 않는다.
 *
 * **메일 단계를 반드시 적는다.** 신청서를 내면 이노페이가 공급사에게 계약 메일을 보내고,
 * 공급사가 그 메일에서 전자계약을 마쳐야 심사로 넘어간다. 이 단계를 적지 않으면 신청서만
 * 내고 메일함에서 멈춘 채 승인을 기다리는 이탈이 생긴다(씨마켓이 지적한 실제 이탈 지점).
 */

interface CardPaymentSignupBannerProps {
  /** 공급사에게는 가입 CTA 를, 발주기관에게는 사정 설명을 보여준다. */
  role: "buyer" | "supplier";
}

export const CardPaymentSignupBanner: React.FC<
  CardPaymentSignupBannerProps
> = ({ role }) => {
  const isSupplier = role === "supplier";

  /**
   * 버튼은 **씨마켓 임베드 안에서만** 그린다.
   *
   * 씨마켓 페이지로 이동시키는 버튼이 아니라, 부모에게 이노페이 가맹 가입 모달을 그 자리에서
   * 열어 달라고 신호를 보내는 버튼이다 — 공급사가 채팅을 벗어나지 않아야 어느 계약 때문에
   * 가입하는지를 잃지 않는다. 임베드 밖(세모를 직접 연 경우)에는 그 신호를 받을 부모가 없어
   * 눌러도 아무 일이 일어나지 않으므로, 죽은 버튼을 그리느니 안내 문구만 남긴다
   * (`SettlementList` 의 [씨마켓 나의 계약 관리 열기] 와 같은 규칙).
   */
  const canOpenSignupModal = isSupplier && isEmbeddedInIframe();

  const handleSignup = () => {
    requestParentNavigate("card-payment-signup");
  };

  return (
    <div className="px-3 pt-3 sm:px-4">
      <div className="rounded-xl border border-amber-300 bg-amber-50 overflow-hidden shadow-sm">
        <div className="px-4 py-3 flex items-center gap-2 border-b border-amber-200/70">
          <CreditCard className="w-4 h-4 text-amber-600 shrink-0" />
          <span className="text-sm font-semibold text-amber-900">
            {isSupplier
              ? "발주기관이 카드 결제를 요청했어요!"
              : "아직 카드로 결제할 수 없어요"}
          </span>
        </div>

        <div className="px-4 py-3">
          <p className="text-xs leading-relaxed text-amber-800">
            {isSupplier
              ? "공급사가 이노페이 가맹점이 아니면 발주기관이 결제를 진행할 수 없어요. 씨마켓에서 카드결제 가입을 신청하고, 이노페이가 보내는 메일에서 전자계약까지 완료해주세요. 심사를 거쳐 승인되기까지 며칠 걸릴 수 있고, 그전까지는 발주기관과 직접 카드로 결제해주세요."
              : "공급사가 아직 씨마켓 카드결제 가맹이 아니라 결제창이 열리지 않습니다. 업체가 가입을 신청해도 이노페이 승인까지 며칠 걸릴 수 있어요. 업체에 가입을 요청하시거나, 업체와 직접 카드로 결제해주세요."}
          </p>

          {canOpenSignupModal && (
            <Button
              onClick={handleSignup}
              size="sm"
              className="mt-3 w-full bg-amber-600 hover:bg-amber-700 text-white"
            >
              이노페이 가입하러 가기
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
